import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { publicAPI, getWebsiteImageUrl } from '../services/api';
import { useTranslation } from '../contexts/TranslationContext';

const Home = () => {
    const { t } = useTranslation();
    const [heroImage, setHeroImage] = useState('/Solarpanele.png');

    // Fetch hero image from API
    useEffect(() => {
        fetchHeroImage();
    }, []);

    const fetchHeroImage = async () => {
        try {
            const response = await publicAPI.getWebsiteImage('hero_background');
            if (response.data.image_path) {
                const imageUrl = getWebsiteImageUrl(response.data.image_path);
                setHeroImage(imageUrl);
            }
        } catch (error) {
            console.error('Error fetching hero image:', error);
        }
    };

    const features = [
        {
            image: "/Photovoltaik.webp",
            title: t('home.feature1Title')
        },
        {
            image: "/Schweißarbeiten.webp",
            title: t('home.feature2Title')
        },
        {
            image: "https://placehold.co/600x400?text=Maschinenf%C3%BChrer",
            title: t('home.feature3Title')
        },
        {
            image: "/Schrauben.webp",
            title: t('home.feature4Title')
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                duration: 0.6
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
    };

    return (
        <div className="min-h-screen bg-white overflow-hidden">
            {/* Hero Section - Mobile optimized */}
            <section className="relative min-h-[100svh] sm:h-screen flex flex-col items-center justify-center text-white">
                <div className="absolute inset-0 z-0">
                    <img
                        src={heroImage}
                        alt="Hero Background"
                        className="w-full h-full object-cover"
                        loading="eager"
                    />
                    <div className="absolute inset-0 bg-black/40"></div>
                </div>

                <div className="container-custom relative z-10 text-center mb-16 sm:mb-32 px-4">
                    <motion.h1
                        className="text-4xl sm:text-6xl md:text-8xl font-bold mb-4"
                        initial={{ opacity: 0, y: -30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        {t('header.companyName')}
                    </motion.h1>
                </div>

                {/* Aufgabenbereich (Overlapping Hero) - Responsive */}
                <div className="absolute bottom-0 left-0 right-0 transform translate-y-1/3 sm:translate-y-1/2 z-20 w-full px-3 sm:px-4">
                    <div className="container-custom">
                        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-4 sm:p-8 md:p-12">
                            <div className="text-center mb-6 sm:mb-10">
                                <h2 className="text-xl sm:text-3xl md:text-4xl font-bold text-neutral-950">
                                    {t('home.taskAreaTitle')}
                                </h2>
                            </div>

                            <motion.div
                                className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6"
                                variants={containerVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, margin: "-50px" }}
                            >
                                {features.map((feature, index) => (
                                    <motion.div
                                        key={index}
                                        variants={itemVariants}
                                        className="group relative overflow-hidden rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg aspect-[3/4] sm:aspect-[4/5] cursor-pointer touch-feedback"
                                    >
                                        <img
                                            src={feature.image}
                                            alt={feature.title}
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                            loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90"></div>
                                        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 md:p-6 text-white">
                                            <h3 className="text-sm sm:text-lg md:text-xl font-bold mb-1 sm:mb-2 line-clamp-2">{feature.title}</h3>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Spacer for the overlapping section - Responsive */}
            <div className="h-48 sm:h-64 md:h-80 bg-neutral-50"></div>

            {/* CTA Section - Mobile optimized */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <div className="bg-neutral-900 rounded-2xl sm:rounded-3xl p-6 sm:p-12 md:p-20 text-center text-white relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-accent-600/20"></div>
                        <div className="relative z-10 max-w-3xl mx-auto">
                            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 sm:mb-6">
                                {t('home.ctaTitle')} <span className="text-primary-400">{t('home.ctaTitleSpan')}</span>
                            </h2>
                            <p className="text-base sm:text-lg md:text-xl text-neutral-300 mb-6 sm:mb-10">
                                {t('home.ctaDescription')}
                            </p>
                            <Link
                                to="/contact"
                                className="inline-block px-6 sm:px-10 py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-blue-500/30 touch-target"
                            >
                                {t('home.ctaButton1')}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
